import { Slider } from '../components/slider';
import { bufferedSegments, seekLimitPct, scrubberValueTextWithLimit } from './scrubber-model';

/**
 * Seek bar. Radix owns pointer capture and the full keyboard/ARIA contract;
 * we add the buffered layer under the played range so the learner can see how
 * far ahead it is safe to drag.
 *
 * `onPreview` fires continuously during a drag and `onCommit` once on release —
 * the distinction matters on YouTube, where a preview maps to
 * `seekTo(s, false)` and does not force a re-buffer on every pixel.
 */
export function Scrubber({
  value,
  max,
  buffered,
  onPreview,
  onCommit,
  disabled,
  seekLimit,
  onSeekBlocked,
}: {
  value: number;
  max: number;
  buffered: Array<[number, number]>;
  onPreview: (seconds: number) => void;
  onCommit: (seconds: number) => void;
  disabled?: boolean;
  /** Furthest seekable second; null = unrestricted. */
  seekLimit?: number | null;
  /** Called when a drag is clamped, so the host can explain why. */
  onSeekBlocked?: () => void;
}) {
  const duration = Number.isFinite(max) && max > 0 ? max : 0;
  const segments = bufferedSegments(buffered, duration);
  const limitPct = seekLimitPct(seekLimit, duration);
  /**
   * Radix's thumb cannot be physically blocked — the slider is controlled, so
   * the clamp happens here and the controlled `value` snaps the thumb back on
   * the next render. Reporting the block lets the player flash why.
   */
  const clamp = (next: number) => {
    if (seekLimit == null || next <= seekLimit) return next;
    onSeekBlocked?.();
    return seekLimit;
  };
  return (
    <Slider
      value={[Math.min(value, duration)]}
      max={duration || 1}
      step={0.1}
      disabled={disabled || duration <= 0}
      aria-label="Seek"
      aria-valuetext={scrubberValueTextWithLimit(value, duration, seekLimit)}
      onValueChange={([next]) => next != null && onPreview(clamp(next))}
      onValueCommit={([next]) => next != null && onCommit(clamp(next))}
      className="py-2"
    >
      {/* The un-watched tail, dimmed. Drawn UNDER the buffered layer so a
          learner can still see how much has loaded beyond the boundary. */}
      {limitPct != null && (
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 bg-black/45"
          style={{ left: `${limitPct}%` }}
        />
      )}
      {segments.map((segment, i) => (
        <div
          key={`${segment.leftPct}-${i}`}
          aria-hidden
          className="absolute inset-y-0 bg-white/40"
          style={{ left: `${segment.leftPct}%`, width: `${segment.widthPct}%` }}
        />
      ))}
    </Slider>
  );
}
